import { InfoOutlined } from "@mui/icons-material";
import { Button, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";

import { formatDate as defaultFormatDate } from "../../../../../ui library/helpers/functions";

const safeISO = (value) => {
  if (!value) {
    return null;
  }
  const d = new Date(value);
  return isNaN(d) ? null : d.toISOString();
};

const getActorName = (actor) => {
  return actor?.name ?? actor?.username ?? actor?.email ?? actor?.user ?? "";
};

export function HistoryTooltipTab({
  historyTab = {},
  formatMask = "MM/dd/yyyy - hh:mm a",
  formatDate = defaultFormatDate,
  placement = "left",
  buttonProps = {},
  tooltipSx = {
    p: 0,
    minWidth: "fit-content",
    width: "fit-content",
    borderRadius: "100%",
  },
}) {
  const { effectiveISO, actorName } = useMemo(() => {
    const uISO = safeISO(historyTab?.updatedAt);
    if (uISO) {
      return {
        effectiveISO: uISO,
        actorName: getActorName(historyTab?.updatedBy ?? historyTab?.updatedByFallback),
      };
    }
    const cISO = safeISO(historyTab?.createdAt);
    if (cISO) {
      return {
        effectiveISO: cISO,
        actorName: getActorName(historyTab?.createdBy),
      };
    }
    return { effectiveISO: null, actorName: "" };
  }, [
    historyTab?.updatedAt,
    historyTab?.updatedBy,
    historyTab?.updatedByFallback,
    historyTab?.createdAt,
    historyTab?.createdBy,
  ]);

  const formatted = useMemo(
    () => (effectiveISO ? formatDate(effectiveISO, formatMask) : ""),
    [effectiveISO, formatDate, formatMask]
  );

  const title = useMemo(
    () => (formatted ? (actorName ? `${formatted} By ${actorName}` : formatted) : ""),
    [formatted, actorName]
  );

  if (!formatted) {
    return null;
  }

  return (
    <div className="flex w-full justify-end">
      <Tooltip sx={tooltipSx} title={title} placement={placement}>
        <Button aria-label="History" {...buttonProps}>
          <InfoOutlined />
        </Button>
      </Tooltip>
    </div>
  );
}

export const HistoryTooltipField = ({ historyData, lostReason = null }) => {
  if (!historyData || !historyData.timestamp) {
    return null;
  }

  const formattedDate = format(new Date(historyData.timestamp), "MM/dd/yyyy - hh:mm a");

  const tooltipText = historyData.user ? `${formattedDate} By ${historyData.user}` : formattedDate;

  return (
    <div className="flex justify-end">
      <Tooltip
        sx={{
          p: 0,
          minWidth: "fit-content",
          width: "fit-content",
          borderRadius: "100%",
        }}
        title={
          lostReason ? (
            <>
              {tooltipText}
              <div>Lost Reason: {lostReason}</div>
            </>
          ) : (
            tooltipText
          )
        }
        placement="left"
      >
        <InfoOutlined fontSize="small" color="primary" className="cursor-pointer" />
      </Tooltip>
    </div>
  );
};

export const HistoryTooltip = ({ historyTab = null, historyData = null, lostReason = null }) => {
  if (historyData) {
    return <HistoryTooltipField historyData={historyData} lostReason={lostReason} />;
  }

  if (historyTab) {
    return <HistoryTooltipTab historyTab={historyTab} />;
  }

  return <></>;
};
